import React, { useEffect, useRef, useState } from 'react';

import { Flex, FlexProps } from '@mantine/core';

interface CustomCanvasRenderingContext2D extends CanvasRenderingContext2D {
  roundRect: (x: number, y: number, w: number, h: number, radius: number) => void;
}

interface HistoryItem {
  value: number;
  timestamp: number;
  height: number;
}

interface LiveAudioVisualizerProps extends FlexProps {
  mediaRecorder: MediaRecorder;
  barWidth?: number;
  barSpacing?: number;
  barColor?: string;
  fftSize?: 32 | 64 | 128 | 256 | 512 | 1024 | 2048 | 4096 | 8192 | 16384 | 32768;
  maxDecibels?: number;
  minDecibels?: number;
  smoothingTimeConstant?: number;
  updateRate?: number;
  historyLength?: number;
  minBarHeight?: number;
  height?: number;
}

// Original code from https://github.com/samhirtarif/react-audio-visualize#readme
// This is a modified version of the original code to fit our needs
// We animate the bars to scroll from right to left and display the audio data
export const LiveAudioVisualizer = ({
  mediaRecorder,
  barWidth = 5,
  barSpacing = 2,
  barColor = '#fff',
  fftSize = 512,
  maxDecibels = -10,
  minDecibels = -100,
  smoothingTimeConstant = 0.3,
  updateRate = 60,
  historyLength = 5500,
  minBarHeight = 2,
  height = 38,
  ...flexProps
}: LiveAudioVisualizerProps) => {
  const [context, setContext] = useState<AudioContext>();
  const [audioSource, setAudioSource] = useState<MediaStreamAudioSourceNode>();
  const [analyser, setAnalyser] = useState<AnalyserNode>();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Track history of audio data
  const [history, setHistory] = useState<HistoryItem[]>([]);
  // Track the last time we updated
  const lastUpdateTimeRef = useRef<number>(0);
  // Animation frame reference
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!mediaRecorder.stream) return;

    const ctx = new AudioContext();
    const analyserNode = ctx.createAnalyser();
    setAnalyser(analyserNode);
    analyserNode.fftSize = fftSize;
    analyserNode.minDecibels = minDecibels;
    analyserNode.maxDecibels = maxDecibels;
    analyserNode.smoothingTimeConstant = smoothingTimeConstant;
    const source = ctx.createMediaStreamSource(mediaRecorder.stream);
    source.connect(analyserNode);
    setContext(ctx);
    setAudioSource(source);

    return () => {
      source.disconnect();
      analyserNode.disconnect();
      ctx.state !== 'closed' && ctx.close();
    };
  }, [mediaRecorder.stream]);

  // Setup animation loop for scrolling visualization
  useEffect(() => {
    if (analyser && mediaRecorder.state === 'recording') {
      const updateVisualization = () => {
        const now = Date.now();

        // Set canvas dimensions directly before drawing
        if (canvasRef.current) {
          // Set canvas drawing dimensions to match display dimensions
          const canvas = canvasRef.current;
          const displayWidth = canvas.clientWidth;
          const displayHeight = canvas.clientHeight;

          // Get the device pixel ratio
          const dpr = window.devicePixelRatio || 1;

          // Set the canvas size accounting for the device pixel ratio
          canvas.width = displayWidth * dpr;
          canvas.height = displayHeight * dpr;

          // Scale the context to match the device pixel ratio
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.scale(dpr, dpr);
          }
        }

        // Only add new data at specified rate
        if (now - lastUpdateTimeRef.current >= updateRate) {
          lastUpdateTimeRef.current = now;

          // Get frequency data
          const data = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(data);

          // Calculate average level
          let sum = 0;
          for (let i = 0; i < data.length; i++) {
            sum += data[i];
          }
          const average = sum / data.length;

          // Find the maximum value in recent history for dynamic scaling
          let maxValue = 1; // Avoid division by zero
          history.forEach((item) => {
            const age = now - item.timestamp;
            if (age <= historyLength && item.value > maxValue) {
              maxValue = item.value;
            }
          });

          // Use the current average as well for more responsive scaling
          maxValue = Math.max(maxValue, average, 30);

          // Calculate the height for this bar (do it once when created)
          const maxHeight = canvasRef.current?.height || height;
          const radius = 2;
          // Make sure bars never reach full height - leave space for rounded corners
          const maxBarHeight = maxHeight - 8; // Leave 8px total space for corners

          // Scale relative to the max value found, with a floor
          // Use both minBarHeight and radius-based minimum to ensure proper rendering
          const scaledHeight = Math.max((average / maxValue) * maxBarHeight, Math.max(minBarHeight, radius * 2));

          // Always add to history even if there's no sound (ensure consistent scrolling)
          setHistory((prev) => {
            // Create new bar with pre-calculated height
            const newHistory = [
              ...prev,
              {
                value: average,
                timestamp: now,
                height: scaledHeight
              }
            ];

            // Trim history if needed
            return newHistory.filter((item) => now - item.timestamp <= historyLength);
          });
        }

        // Draw all history
        if (canvasRef.current) {
          drawScrollingBars(history, canvasRef.current, barWidth, barColor, historyLength, barSpacing);
        }

        // Continue animation loop
        animationFrameRef.current = requestAnimationFrame(updateVisualization);
      };

      // Start animation
      animationFrameRef.current = requestAnimationFrame(updateVisualization);

      // Cleanup
      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }
  }, [analyser, mediaRecorder.state, history]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (context && context.state !== 'closed') {
        context.close();
      }
      audioSource?.disconnect();
      analyser?.disconnect();
    };
  }, []);

  const drawScrollingBars = (
    history: HistoryItem[],
    canvas: HTMLCanvasElement,
    barWidth: number,
    barColor: string,
    maxDisplayTime: number,
    barSpacing: number
  ): void => {
    const ctx = canvas.getContext('2d', { alpha: false }) as CustomCanvasRenderingContext2D;
    if (!ctx) return;

    // Get the device pixel ratio
    const dpr = window.devicePixelRatio || 1;

    // Set canvas dimensions accounting for device pixel ratio
    const displayWidth = canvas.clientWidth;
    const displayHeight = canvas.clientHeight;
    canvas.width = displayWidth * dpr;
    canvas.height = displayHeight * dpr;

    // Scale the context to match the device pixel ratio
    ctx.scale(dpr, dpr);

    // Clear the canvas
    ctx.clearRect(0, 0, displayWidth, displayHeight);

    const now = Date.now();
    const totalWidth = displayWidth;
    const maxHeight = displayHeight;
    const barStep = barWidth + barSpacing;
    const scrollSpeed = barStep / updateRate;
    const radius = 2;

    // Draw from right to left (newest data on right)
    history.forEach((item) => {
      const age = now - item.timestamp;
      if (age > maxDisplayTime) return;

      const x = totalWidth - age * scrollSpeed;
      if (x < -barWidth || x > totalWidth) return;

      const value = item.height;
      ctx.fillStyle = barColor;

      // Ensure bars fit within canvas with margin for rounded corners
      const margin = radius + 1; // Extra margin to ensure corners are visible
      const maxAllowedHeight = maxHeight - margin * 2;
      const actualHeight = Math.min(value, maxAllowedHeight);

      // Center the bar vertically within the safe area
      const y = margin + (maxAllowedHeight - actualHeight) / 2;
      const h = actualHeight;

      // Draw rounded rectangle manually for consistent rendering
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + barWidth - radius, y);
      ctx.quadraticCurveTo(x + barWidth, y, x + barWidth, y + radius);
      ctx.lineTo(x + barWidth, y + h - radius);
      ctx.quadraticCurveTo(x + barWidth, y + h, x + barWidth - radius, y + h);
      ctx.lineTo(x + radius, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
      ctx.fill();
    });
  };

  return (
    <Flex w="100%" h={height} {...flexProps}>
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          aspectRatio: 'unset'
        }}
      />
    </Flex>
  );
};
